import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, IconButton, Button, TextField, Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { TEAM_COLOR_PRESETS } from './mockTeams';
import { driversForShift } from './mockRoster';
import { C } from './styles';

function TeamsManagerModal({ open, onClose, teams, onChange, shift = 'AM' }) {
  const [editingId, setEditingId] = useState(null);
  const allDrivers = useMemo(() => driversForShift(shift), [shift]);

  const editingTeam = teams.find(t => t.id === editingId);

  const updateTeam = (id, patch) => {
    onChange(teams.map(t => t.id === id ? { ...t, ...patch } : t));
  };

  const addTeam = () => {
    const id = `team-${Date.now()}`;
    const usedColors = new Set(teams.map(t => t.color));
    const nextColor = TEAM_COLOR_PRESETS.find(p => !usedColors.has(p.value))?.value || TEAM_COLOR_PRESETS[0].value;
    const newTeam = { id, name: 'New Team', color: nextColor, driverIds: [] };
    onChange([...teams, newTeam]);
    setEditingId(id);
  };

  const deleteTeam = (id) => {
    onChange(teams.filter(t => t.id !== id));
    if (editingId === id) setEditingId(null);
  };

  const removeDriver = (teamId, driverId) => {
    const team = teams.find(t => t.id === teamId);
    updateTeam(teamId, { driverIds: team.driverIds.filter(d => d !== driverId) });
  };

  const addDrivers = (teamId, newDriverIds) => {
    const team = teams.find(t => t.id === teamId);
    const merged = Array.from(new Set([...team.driverIds, ...newDriverIds.map(d => d.id)]));
    updateTeam(teamId, { driverIds: merged });
  };

  // Drivers not yet in the editing team
  const driverPickerOptions = editingTeam
    ? allDrivers.filter(d => !editingTeam.driverIds.includes(d.id))
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(20, 20, 25, 0.97)',
          backdropFilter: C.blur,
          WebkitBackdropFilter: C.blur,
          border: `1px solid ${C.border}`,
          color: C.textPrimary,
        },
      }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        borderBottom: `1px solid ${C.border}`, py: 1.5,
      }}>
        <GroupsIcon sx={{ color: C.accent }} />
        <Box sx={{ flex: 1 }}>
          <Typography component="span" sx={{ color: C.accent, fontWeight: 700, fontSize: '1rem' }}>
            Teams
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: C.textMuted, mt: 0.25 }}>
            Group drivers to run together — colored on the grid.
          </Typography>
        </Box>
        <Button
          onClick={addTeam}
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          sx={{
            color: C.accent, borderColor: C.accent,
            textTransform: 'none', fontWeight: 600, fontSize: '0.75rem',
            '&:hover': { borderColor: C.accent, backgroundColor: C.accentBg },
          }}
        >
          New Team
        </Button>
        <IconButton onClick={onClose} size="small" sx={{ color: C.textMuted, ml: 1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', gap: 0, p: 0, minHeight: 420 }}>
        {/* Team list */}
        <Box sx={{
          width: 240,
          borderRight: `1px solid ${C.border}`,
          overflowY: 'auto',
          backgroundColor: 'rgba(0,0,0,0.18)',
        }}>
          {teams.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.75rem', color: C.textMuted, fontStyle: 'italic' }}>
                No teams yet. Tap "New Team" above.
              </Typography>
            </Box>
          )}
          {teams.map((team) => {
            const isActive = team.id === editingId;
            return (
              <Box
                key={team.id}
                onClick={() => setEditingId(team.id)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1.5, py: 1.25,
                  cursor: 'pointer',
                  borderBottom: `1px solid ${C.border}`,
                  backgroundColor: isActive ? C.accentBg : 'transparent',
                  borderLeft: `4px solid ${isActive ? C.accent : 'transparent'}`,
                  '&:hover': { backgroundColor: isActive ? C.accentBg : 'rgba(255,255,255,0.04)' },
                }}
              >
                <Box sx={{
                  width: 14, height: 14, borderRadius: '50%',
                  backgroundColor: team.color,
                  border: `1px solid ${C.border}`,
                  flexShrink: 0,
                }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: '0.8rem', color: C.textPrimary, fontWeight: 600 }}>
                    {team.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: C.textMuted }}>
                    {team.driverIds.length} driver{team.driverIds.length === 1 ? '' : 's'}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Edit panel */}
        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {!editingTeam ? (
            <Box sx={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography sx={{ color: C.textMuted, fontSize: '0.85rem' }}>
                Select a team on the left, or create a new one.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Name + delete */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  value={editingTeam.name}
                  onChange={(e) => updateTeam(editingTeam.id, { name: e.target.value })}
                  size="small"
                  fullWidth
                  placeholder="Team name"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: C.textPrimary,
                      backgroundColor: 'rgba(0,0,0,0.25)',
                      '& fieldset': { borderColor: C.border },
                      '&:hover fieldset': { borderColor: C.accent },
                      '&.Mui-focused fieldset': { borderColor: C.accent },
                    },
                  }}
                />
                <IconButton
                  onClick={() => deleteTeam(editingTeam.id)}
                  sx={{ color: '#FF6B6B', '&:hover': { backgroundColor: 'rgba(239,68,68,0.12)' } }}
                  title="Delete team"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>

              {/* Color picker */}
              <Box>
                <Typography sx={{ fontSize: '0.6rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                  Color
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                  {TEAM_COLOR_PRESETS.map((preset) => {
                    const selected = editingTeam.color === preset.value;
                    return (
                      <Box
                        key={preset.value}
                        onClick={() => updateTeam(editingTeam.id, { color: preset.value })}
                        sx={{
                          width: 28, height: 28, borderRadius: 1,
                          backgroundColor: preset.value,
                          cursor: 'pointer',
                          border: `2px solid ${selected ? C.accent : 'rgba(255,255,255,0.18)'}`,
                          transition: 'transform 80ms',
                          '&:hover': { transform: 'scale(1.08)' },
                        }}
                        title={preset.label}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* Member list */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Typography sx={{ fontSize: '0.6rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                  Members ({editingTeam.driverIds.length})
                </Typography>
                <Box sx={{
                  flex: 1, overflowY: 'auto', minHeight: 80, maxHeight: 240,
                  border: `1px solid ${C.border}`,
                  borderRadius: 1,
                  backgroundColor: 'rgba(0,0,0,0.18)',
                }}>
                  {editingTeam.driverIds.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.75rem', color: C.textMuted, fontStyle: 'italic' }}>
                        No members yet. Add drivers below.
                      </Typography>
                    </Box>
                  ) : (
                    editingTeam.driverIds.map((id) => {
                      const d = allDrivers.find(x => x.id === id);
                      return (
                        <Box key={id} sx={{
                          display: 'flex', alignItems: 'center', gap: 1,
                          px: 1.5, py: 0.75,
                          borderBottom: `1px solid ${C.border}`,
                          '&:last-child': { borderBottom: 'none' },
                        }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.78rem', color: C.textPrimary, fontWeight: 600 }}>
                              {d ? d.displayName : id}
                              {d && (
                                <Box component="span" sx={{ color: C.textMuted, fontWeight: 400, ml: 0.5 }}>
                                  ({d.seniority})
                                </Box>
                              )}
                            </Typography>
                            {d && (
                              <Typography sx={{ fontSize: '0.6rem', color: C.textMuted }}>
                                {d.shiftCode} · Badge {d.badge}
                              </Typography>
                            )}
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => removeDriver(editingTeam.id, id)}
                            sx={{ color: C.textMuted, '&:hover': { color: '#FF6B6B' } }}
                          >
                            <RemoveCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Box>

              {/* Add driver picker */}
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={driverPickerOptions}
                getOptionLabel={(d) => `${d.displayName} (${d.seniority}) — Badge ${d.badge}`}
                value={[]}
                onChange={(_, value) => {
                  if (value.length > 0) addDrivers(editingTeam.id, value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Add drivers from AM shift…"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: C.textPrimary,
                        backgroundColor: 'rgba(0,0,0,0.25)',
                        '& fieldset': { borderColor: C.border },
                        '&:hover fieldset': { borderColor: C.accent },
                        '&.Mui-focused fieldset': { borderColor: C.accent },
                      },
                    }}
                  />
                )}
                componentsProps={{
                  paper: {
                    sx: {
                      backgroundColor: 'rgba(20,20,25,0.98)',
                      color: C.textPrimary,
                      border: `1px solid ${C.border}`,
                    },
                  },
                }}
              />
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${C.border}`, py: 1, px: 2 }}>
        <Button
          onClick={onClose}
          sx={{ color: C.textSecondary, textTransform: 'none' }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TeamsManagerModal;
